import type { GenericLampState } from '@eagle/data-function-types';

export enum FaultStatusValue {
  Active = 'active',
  Cleared = 'cleared',
  Unknown = 'unknown',
}

export enum WarningValue {
  RedStop = 'red-stop',
  Protect = 'protect',
  Amber = 'amber',
  None = 'none',
  Unknown = 'unknown',
}

export interface GetThingFaultSummaryInput {
  thingId: string;
  range?: number;
  status?: FaultStatusValue;
  warning?: WarningValue;
}

export interface FaultEpisode {
  startOccurred: string;
  endOccurred?: string;
  activeAtRangeStart: boolean;
  activeAtRangeEnd: boolean;
  count: number;
}

export interface ConsolidatedFault {
  id: string;
  thingId: string;
  protocol: string;
  sourceAddress: string;
  code: string;
  failureModeIdentifier: string;
  display?: string;
  status: FaultStatusValue;
  warning: WarningValue;
  timing: {
    firstOccurred: string;
    lastOccurred: string;
    daysSinceFirst: number;
    daysSinceLast: number;
  };

  counts: {
    eventCount: number;
    activeCount: number;
    inactiveCount: number;
    episodeCount: number;
  };

  behaviour: {
    eventsPerDay: number;
    becameActiveDuringRange: boolean;
    clearedDuringRange: boolean;
  };

  latestEvent: {
    occurred: string;
    active: boolean;
    data: Record<string, unknown>;
  };

  episodes: FaultEpisode[];
}

export interface FaultSummaryTotals {
  totalFaults: number;
  activeCount: number;
  clearedCount: number;
  unknownCount: number;
  warningCounts: Record<WarningValue, number>;
  totalEventCount: number;
}

export interface RangeMetadata {
  rangeStart: string;
  rangeEnd: string;
  daysRequested: number;
}

export interface ProcessingMetadata {
  eventCount: number;
  pagesFetched: number;
  processingTimeMs: number;
  warnings?: string[];
}

export interface GetThingFaultSummaryResult {
  range: RangeMetadata;
  totals: FaultSummaryTotals;
  faults: ConsolidatedFault[];
  meta: ProcessingMetadata;
}

export interface RawFaultEvent {
  _id: string;
  thingId: string;
  feature: string;
  featureTypeId: string;
  eventTypeId: string;
  occurred: string;
  inserted: string;
  data: FaultEventData;
}

export interface FaultEventData {
  active: boolean;
  code: string;
  failureModeIdentifier: string;
  protocol: string;
  sourceAddress: string;
  occurrenceCount?: number;
  redStop?: boolean | GenericLampState;
  protect?: boolean | GenericLampState;
  amberWarning?: boolean | GenericLampState;
  [key: string]: unknown;
}

export type FaultGroupKey = string;

export interface ThingEventPaginatedResponse {
  items: RawFaultEvent[];
  count: number;
  hasMore: boolean;
}

export interface ValidationError {
  field: string;
  message: string;
}
